import { ProductKeys } from '@config/keys/product.keys';
import { DoodProductModel } from '@core/models/product.model';
import { IAdditionGroup } from '@core/models/shop.model';

export class AutoFillCartItemRulesAdditionsGroup {
  // ! It only works with additions group that have only one product, but it can be extended to work with multiple products
  static hasExclusiveAdditionInAdditionsGroup({
    additionGroup,
  }: {
    additionGroup: IAdditionGroup;
  }) {
    const hasDefinedQuantity = !!(additionGroup.min_count && additionGroup.max_count);
    const isMinCountEqualToMaxCount = additionGroup.min_count === additionGroup.max_count;
    const hasOnlyOneAddition = additionGroup.items.length === 1;

    return hasDefinedQuantity && isMinCountEqualToMaxCount && hasOnlyOneAddition;
  }

  /** Groups the customer still has to choose from. Exclusive groups are prefilled and hidden. */
  static navigableAdditionGroups(additionGroups: IAdditionGroup[]): IAdditionGroup[] {
    return additionGroups.filter(
      additionGroup => !this.hasExclusiveAdditionInAdditionsGroup({ additionGroup }),
    );
  }

  /**
   * Keeps an explicit navigable selection. An exclusive or unknown id falls back to the
   * first group that still requires a choice.
   */
  static resolveNavigableAdditionGroupId(
    additionGroups: IAdditionGroup[] | undefined,
    selectedId: string | undefined,
  ): string | undefined {
    const navigableGroups = this.navigableAdditionGroups(additionGroups ?? []);
    if (selectedId && navigableGroups.some(additionGroup => additionGroup.id === selectedId)) {
      return selectedId;
    }
    return navigableGroups[0]?.id;
  }

  static nextNavigableAdditionGroupId(
    additionGroups: IAdditionGroup[],
    currentGroupId: string | undefined,
  ): string | undefined {
    const navigableGroups = this.navigableAdditionGroups(additionGroups);
    const currentIndex = navigableGroups.findIndex(
      additionGroup => additionGroup.id === currentGroupId,
    );
    if (currentIndex === -1) {
      return navigableGroups[0]?.id;
    }
    return navigableGroups[currentIndex + 1]?.id;
  }

  static previousNavigableAdditionGroupId(
    additionGroups: IAdditionGroup[],
    currentGroupId: string | undefined,
  ): string | undefined {
    const navigableGroups = this.navigableAdditionGroups(additionGroups);
    const currentIndex = navigableGroups.findIndex(
      additionGroup => additionGroup.id === currentGroupId,
    );
    if (currentIndex <= 0) {
      return undefined;
    }
    return navigableGroups[currentIndex - 1]?.id;
  }

  /** Appends missing exclusive selections without duplicating ids the customer already has. */
  static mergeExclusiveAutoFilledAdditionIds(
    existingIds: string[] | undefined,
    additionGroups: IAdditionGroup[],
  ): string[] {
    const existing = existingIds ?? [];
    const missingIds = additionGroups.flatMap(additionGroup => {
      if (!this.hasExclusiveAdditionInAdditionsGroup({ additionGroup })) {
        return [];
      }
      const itemId = additionGroup.items[0]?.id;
      if (!itemId) {
        return [];
      }
      const alreadySelected = existing.filter(id => id === itemId).length;
      const minCount = additionGroup.min_count || 1;
      const missingCount = Math.max(0, minCount - alreadySelected);
      return Array(missingCount).fill(itemId) as string[];
    });
    return [...existing, ...missingIds];
  }

  /**
   * Ids to put on a direct kiosk add when every addition group is a single mandatory item.
   * Null means the customer still has a choice, so the composition screen must open.
   */
  static exclusiveAutoFilledAdditionIdsForProduct({
    product,
    shopProducts,
    additionGroups,
  }: {
    product: DoodProductModel;
    shopProducts: DoodProductModel[] | undefined;
    additionGroups: IAdditionGroup[] | undefined;
  }): string[] | null {
    if (product.type !== 'SIMPLE') {
      return null;
    }
    const additionIds = product.additions?.products ?? [];
    if (additionIds.length === 0 || !shopProducts || !additionGroups) {
      return null;
    }

    const productsById = new Map(shopProducts.map(item => [item.id, item]));
    const groupsById = new Map(
      additionGroups.map(additionGroup => [additionGroup.id, additionGroup]),
    );
    const built = new Map<string, IAdditionGroup>();

    for (const additionId of additionIds) {
      const addition = productsById.get(additionId);
      if (!addition || addition.available === false || addition.hidden) {
        return null;
      }
      const source = groupsById.get(addition[ProductKeys.AdditionsGroup]);
      if (!source) {
        return null;
      }
      const existing = built.get(source.id);
      if (existing) {
        existing.items.push(addition);
        continue;
      }
      built.set(source.id, {
        ...source,
        items: [addition],
      });
    }

    const groups = [...built.values()];
    if (
      groups.length === 0 ||
      groups.some(additionGroup => !this.hasExclusiveAdditionInAdditionsGroup({ additionGroup }))
    ) {
      return null;
    }
    return this.getAutoFilledAdditionIds(groups);
  }

  static getAutoFilledAdditionIds(additionGroups: IAdditionGroup[]): string[] {
    return additionGroups.reduce((acc, additionGroup) => {
      if (this.hasExclusiveAdditionInAdditionsGroup({ additionGroup })) {
        const exclusiveAddition = additionGroup.items[0];
        if (exclusiveAddition.id) {
          const minCount = additionGroup.min_count || 1;
          const additionIds = Array(minCount).fill(exclusiveAddition.id);
          return [...acc, ...additionIds];
        }
      }
      return acc;
    }, [] as string[]);
  }

  /** Shape expected by declinable store (`selected` / `isValid` / `selectedItems`). */
  static getAutoFilledAdditionsByGroup(
    additionGroups: IAdditionGroup[] | undefined,
  ): IAdditionGroup[] {
    if (!additionGroups?.length) {
      return [];
    }

    return additionGroups.reduce((acc, additionGroup) => {
      if (!this.hasExclusiveAdditionInAdditionsGroup({ additionGroup })) {
        return acc;
      }

      const exclusiveAddition = additionGroup.items[0];
      if (!exclusiveAddition?.id) {
        return acc;
      }

      const minCount = additionGroup.min_count || 1;
      return [
        ...acc,
        {
          ...additionGroup,
          selected: Array(minCount).fill(exclusiveAddition.id),
          isValid: true,
          count: minCount,
          selectedItems: [{ ...exclusiveAddition, quantity: minCount }],
        },
      ];
    }, [] as IAdditionGroup[]);
  }

  static applyExclusiveAutoFillToAdditionGroups(
    additionGroups: IAdditionGroup[] | undefined,
  ): IAdditionGroup[] | undefined {
    if (!additionGroups) {
      return additionGroups;
    }

    return additionGroups.map(additionGroup => {
      if (!this.hasExclusiveAdditionInAdditionsGroup({ additionGroup })) {
        return additionGroup;
      }

      const exclusiveAddition = additionGroup.items[0];
      if (!exclusiveAddition?.id) {
        return additionGroup;
      }

      const minCount = additionGroup.min_count || 1;
      return {
        ...additionGroup,
        selected: Array(minCount).fill(exclusiveAddition.id),
        isValid: true,
        count: minCount,
        selectedItems: [{ ...exclusiveAddition, quantity: minCount }],
      };
    });
  }
}
